// externals
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internals
import { fetchRecentDeals, recentDealsSelectors } from '../store';

/**
 * Hook responsible for providing the fetch function for recent deals data.
 * Follows Single Responsibility Principle - only knows HOW to fetch, not WHEN.
 *
 * The orchestrator hook (useRecentDeals) controls when fetching occurs:
 * - Onload: via useEffect with useRef guard
 * - On-demand: via dealership change handler
 *
 * @returns refetchDeals function to trigger a fetch for a specific dealership
 */
export const useFetchRecentDeals = (): { refetchDeals: (coOrgId: string, mmdId: string) => void } => {
    const dispatch = useDispatch();
    const { getRecentDealsURL } = recentDealsSelectors.additionalSelectors;

    const bffEndpoint = useSelector(getRecentDealsURL);

    /**
     * Fetches recent deals for a specific dealership.
     * Can be called on initial load or on-demand (e.g., dealership switch).
     */
    const refetchDeals = useCallback(
        (coOrgId: string, mmdId: string) => {
            if (bffEndpoint) {
                dispatch(fetchRecentDeals(bffEndpoint, coOrgId, mmdId));
            }
        },
        [bffEndpoint, dispatch]
    );

    return { refetchDeals };
};
